import { Colors } from "@/constants/colors";
import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";

const fontColors = Colors.text;

const fontSize = {
  "3xl": {
    fontSize: "text-3xl",
    lineHeight: "leading-[1.2]",
  },
  "2xl": {
    fontSize: "text-2xl",
    lineHeight: "leading-[1.2]",
  },
  xl: {
    fontSize: "text-xl",
    lineHeight: "leading-[1.25]",
  },
  lg: {
    fontSize: "text-lg",
    lineHeight: "leading-[1.2]",
  },
  md: {
    fontSize: "text-md",
    lineHeight: "leading-[1.3]",
  },
  sm: {
    fontSize: "text-sm",
    lineHeight: "leading-[1.2]",
  },
  xs: {
    fontSize: "text-xs",
    lineHeight: "leading-[1.2]",
  },
  "2xs": {
    fontSize: "text-2xs",
    lineHeight: "leading-[1.3]",
  },
  paragraphMd: {
    fontSize: "text-md",
    lineHeight: "leading-[1.4]",
  },
  paragraphSm: {
    fontSize: "text-sm",
    lineHeight: "leading-[1.4]",
  },
  paragraphXs: {
    fontSize: "text-xs",
    lineHeight: "leading-[1.4]",
  },
} as const;

const fontWeight = {
  350: "font-light",
  400: "font-normal",
  500: "font-medium",
  600: "font-semibold",
  700: "font-bold",
} as const;

const fontFamily = {
  unbounded: "font-unbounded",
  inter: "font-inter",
  fanwood: "font-fanwood",
  spring: "font-spring",
} as const;

export interface TypographyProps extends Omit<React.ComponentProps<"p">, "size"> {
  asChild?: boolean;
  size?: keyof typeof fontSize;
  weight?: keyof typeof fontWeight;
  color?: keyof typeof fontColors;
  font?: keyof typeof fontFamily;
  underline?: boolean;
}

export function Typography({
  children,
  className,
  asChild = false,
  size,
  weight = 400,
  color,
  font = "inter",
  underline = false,
  ...props
}: TypographyProps) {
  const Comp = asChild ? Slot : "div";

  return (
    <Comp
      className={cn(
        color != null ? fontColors[color] : "",
        size != null ? fontSize[size].fontSize : "",
        size != null ? fontSize[size].lineHeight : "",
        fontWeight[weight],
        fontFamily[font],
        underline && "underline decoration-solid decoration-skip-ink-none",
        {
          "leading-[1.4]": font === "unbounded",
        },
        className,
      )}
      {...props}
    >
      {children}
    </Comp>
  );
}
